import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { Badge } from "@mantine/core";
import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";

import { useFileManager, useFileManagerUI } from "@/components/core/FileManager";
import type { FileManagerItem } from "@/components/core/FileManager";
import { getIconConfig } from "../FileManagerItemCard/FileManagerItemCard";

import styles from "./FileManagerDndProvider.module.scss";

export const FileManagerDndActive = createContext(false);

export function useFileManagerDndActive() {
  return useContext(FileManagerDndActive);
}

export function FileManagerDragOverlayCard({
  item,
  count,
}: {
  item: FileManagerItem;
  count: number;
}) {
  const { Icon, color } = getIconConfig(item);
  return (
    <div className={styles.overlayCard}>
      <Icon size={24} color={color} />
      <span className={styles.overlayName}>{item.name}</span>
      {count > 1 && (
        <Badge size="xs" circle>
          {count}
        </Badge>
      )}
    </div>
  );
}

export function useFileManagerDndHandlers() {
  const { moveItems } = useFileManager();
  const { selectedItems, isSelected, exitSelectionMode } = useFileManagerUI();

  const [activeItem, setActiveItem] = useState<FileManagerItem | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const dragCount = activeItem && isSelected(activeItem.id) ? selectedItems.length : 1;

  function handleDragStart({ active }: DragStartEvent) {
    setActiveItem((active.data.current?.item as FileManagerItem | undefined) ?? null);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveItem(null);
    if (!over) return;

    const dragged = active.data.current?.item as FileManagerItem | undefined;
    if (!dragged) return;

    const droppableData = over.data.current ?? {};
    const target = (droppableData as { item?: FileManagerItem }).item;
    const isColumnZoneDrop = "colFolderId" in droppableData;
    const colFolderId = (droppableData as { colFolderId?: string | null }).colFolderId;

    let destinationId: string | null;
    let destinationName: string | null;

    if (target?.type === "FOLDER") {
      if (target.id === dragged.id) return;
      if (target.parentId === dragged.id) return;
      if (target.id === dragged.parentId) return;
      destinationId = target.id;
      destinationName = target.name;
    } else if (isColumnZoneDrop) {
      const resolvedColId = colFolderId ?? null;
      if (resolvedColId === (dragged.parentId ?? null)) return;
      destinationId = resolvedColId;
      destinationName = null;
    } else {
      return;
    }

    const itemsToMove = isSelected(dragged.id) ? selectedItems : [dragged];
    const filtered = itemsToMove.filter((i) => i.id !== destinationId);

    if (filtered.length === 0) return;

    moveItems(
      {
        items: filtered.map((i) => ({ id: i.id, name: i.name, type: i.type })),
        destination: { id: destinationId, name: destinationName },
      },
      { onSuccess: () => exitSelectionMode(), onError: () => {} },
    );
  }

  return { activeItem, dragCount, sensors, handleDragStart, handleDragEnd };
}

export function FileManagerDndProvider({ children }: { children: ReactNode }) {
  const { activeItem, dragCount, sensors, handleDragStart, handleDragEnd } =
    useFileManagerDndHandlers();

  return (
    <FileManagerDndActive.Provider value={true}>
      <DndContext
        collisionDetection={pointerWithin}
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {children}
        <DragOverlay dropAnimation={null}>
          {activeItem ? <FileManagerDragOverlayCard item={activeItem} count={dragCount} /> : null}
        </DragOverlay>
      </DndContext>
    </FileManagerDndActive.Provider>
  );
}
